// ============================================================
// src/pages/ReportsPage.jsx  [Supervisor]
// No extra packages needed — custom calendar built from scratch
// ============================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getWeeklySummary, getExportPreview, downloadExcel, downloadPDF } from '../api/reportApi';
import { Toast, Spinner } from '../components/UI';
import { useToast } from '../hooks/useToast';
import { getEmployees } from '../api/employeeApi';
import {
  getCurrentWeekEnding, formatFromDate, parseDate, toDisplay,
} from '../utils/dateHelper';

const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];
const DAYS   = ['Su','Mo','Tu','We','Th','Fr','Sa'];

// ── Custom Calendar Picker (zero dependencies) ───────────────
function DatePicker({ value, onChange, small = false }) {
  const selected   = parseDate(value);
  const today      = new Date();
  const [open, setOpen]         = useState(false);
  const [viewYear, setViewYear] = useState((selected || today).getFullYear());
  const [viewMonth, setViewMonth] = useState((selected || today).getMonth());
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handle(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  // Sync view when value changes externally
  useEffect(() => {
    if (selected) { setViewYear(selected.getFullYear()); setViewMonth(selected.getMonth()); }
  }, [value]);

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }

  // Build calendar grid
  function buildDays() {
    const first = new Date(viewYear, viewMonth, 1).getDay();
    const total = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(d);
    return cells;
  }

  function selectDay(day) {
    if (!day) return;
    const dt = new Date(viewYear, viewMonth, day);
    onChange(formatFromDate(dt));
    setOpen(false);
  }

  function isSelected(day) {
    if (!day || !selected) return false;
    return selected.getFullYear() === viewYear &&
           selected.getMonth()    === viewMonth &&
           selected.getDate()     === day;
  }
  function isToday(day) {
    return day &&
      today.getFullYear() === viewYear &&
      today.getMonth()    === viewMonth &&
      today.getDate()     === day;
  }

  // Year range for dropdown
  const years = [];
  for (let y = today.getFullYear() - 5; y <= today.getFullYear() + 5; y++) years.push(y);

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>

      {/* ── Trigger input ── */}
      <div style={{ position: 'relative', width: '100%' }}>
        <input
          type="text"
          readOnly
          value={value || ''}
          placeholder="yyyy-mm-dd"
          onClick={() => setOpen(o => !o)}
          style={{
            width: '100%', boxSizing: 'border-box',
            border: `1.5px solid ${open ? '#dc2626' : '#d1d5db'}`,
            borderRadius: '6px',
            padding: small ? '0.28rem 2.2rem 0.28rem 0.55rem' : '0.5rem 2.4rem 0.5rem 0.75rem',
            fontSize: small ? '0.8rem' : '0.875rem',
            color: value ? '#111827' : '#9ca3af',
            background: '#fff', outline: 'none', cursor: 'pointer',
            transition: 'border-color 0.15s',
          }}
        />
        <span
          onClick={() => setOpen(o => !o)}
          style={{
            position: 'absolute', right: '0.55rem', top: '50%',
            transform: 'translateY(-50%)',
            fontSize: small ? '0.9rem' : '1rem',
            cursor: 'pointer', userSelect: 'none', lineHeight: 1,
          }}
        >📅</span>
      </div>

      {/* ── Calendar popup — fixed position, always visible ── */}
      {open && (
        <div
          style={{
            position: 'fixed',
            // Will be repositioned by the useEffect below
            top: 0, left: 0,
            zIndex: 99999,
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
            width: '288px',
            userSelect: 'none',
          }}
          ref={el => {
            // Position below the input, flip up if near bottom
            if (!el || !wrapRef.current) return;
            const rect = wrapRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            const popH = el.offsetHeight || 300;
            if (spaceBelow >= popH + 8 || spaceBelow >= spaceAbove) {
              el.style.top  = `${rect.bottom + 6}px`;
            } else {
              el.style.top  = `${rect.top - popH - 6}px`;
            }
            // Horizontal: align left but keep within viewport
            let left = rect.left;
            if (left + 288 > window.innerWidth - 8) left = window.innerWidth - 288 - 8;
            if (left < 8) left = 8;
            el.style.left = `${left}px`;
          }}
        >
          {/* Header */}
          <div style={{
            padding: '12px 14px 8px',
            borderBottom: '1px solid #f3f4f6',
          }}>
            {/* Month / Year dropdowns */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <button onClick={prevMonth} style={navBtn}>‹</button>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <select
                  value={viewMonth}
                  onChange={e => setViewMonth(+e.target.value)}
                  style={dropdownStyle}
                >
                  {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
                </select>
                <select
                  value={viewYear}
                  onChange={e => setViewYear(+e.target.value)}
                  style={dropdownStyle}
                >
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <button onClick={nextMonth} style={navBtn}>›</button>
            </div>

            {/* Day-of-week headers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
              {DAYS.map(d => (
                <div key={d} style={{
                  textAlign: 'center', fontSize: '0.62rem', fontWeight: 700,
                  color: '#9ca3af', textTransform: 'uppercase',
                  lineHeight: '26px',
                }}>{d}</div>
              ))}
            </div>
          </div>

          {/* Day grid */}
          <div style={{ padding: '8px 10px 12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
              {buildDays().map((day, i) => (
                <div
                  key={i}
                  onClick={() => selectDay(day)}
                  style={{
                    height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: '7px', fontSize: '0.82rem', cursor: day ? 'pointer' : 'default',
                    fontWeight: isSelected(day) ? 700 : isToday(day) ? 700 : 400,
                    background: isSelected(day) ? '#dc2626' : 'transparent',
                    color: isSelected(day) ? '#fff' : isToday(day) ? '#dc2626' : day ? '#374151' : 'transparent',
                    transition: 'background 0.1s, color 0.1s',
                  }}
                  onMouseEnter={e => {
                    if (day && !isSelected(day)) {
                      e.currentTarget.style.background = '#fee2e2';
                      e.currentTarget.style.color = '#dc2626';
                    }
                  }}
                  onMouseLeave={e => {
                    if (day && !isSelected(day)) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = isToday(day) ? '#dc2626' : '#374151';
                    }
                  }}
                >
                  {day || ''}
                </div>
              ))}
            </div>

            {/* Today shortcut */}
            <div style={{ textAlign: 'center', marginTop: '8px', borderTop: '1px solid #f3f4f6', paddingTop: '8px' }}>
              <button
                onClick={() => { onChange(formatFromDate(today)); setOpen(false); }}
                style={{
                  background: 'none', border: '1px solid #e5e7eb', borderRadius: '5px',
                  padding: '3px 14px', fontSize: '0.75rem', fontWeight: 600,
                  color: '#374151', cursor: 'pointer',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#dc2626'; e.currentTarget.style.color = '#dc2626'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; }}
              >
                Today
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const navBtn = {
  background: 'none', border: '1px solid #e5e7eb', borderRadius: '5px',
  width: '28px', height: '28px', cursor: 'pointer', display: 'flex',
  alignItems: 'center', justifyContent: 'center',
  fontSize: '1.1rem', color: '#6b7280', lineHeight: 1,
};
const dropdownStyle = {
  fontSize: '0.78rem', fontWeight: 600,
  border: '1px solid #e5e7eb', borderRadius: '5px',
  padding: '3px 6px', color: '#374151', background: '#f9fafb',
  outline: 'none', cursor: 'pointer',
};

// ════════════════════════════════════════════════════════════
export default function ReportsPage() {
  const { toast, showToast, clearToast } = useToast();

  const [summaryDate,  setSummaryDate]  = useState(getCurrentWeekEnding());
  const [summaryRows,  setSummaryRows]  = useState([]);
  const [loadingSum,   setLoadingSum]   = useState(false);

  const [dateRange,    setDateRange]    = useState('weekly');
  const [exportStart,  setExportStart]  = useState('');
  const [exportEnd,    setExportEnd]    = useState('');
  const [filterEmp,    setFilterEmp]    = useState('');
  const [groupBy,      setGroupBy]      = useState('employee');
  const [mechanics,    setMechanics]    = useState([]);
  const [exportRows,   setExportRows]   = useState([]);
  const [loadingExp,   setLoadingExp]   = useState(false);
  const [showPreview,  setShowPreview]  = useState(false);

  const toHourMinuteString = totalMinutes => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}.${String(minutes).padStart(2, '0')}`;
  };

  const toMinutes = value => {
    if (value === null || value === undefined || value === '') return 0;
    const [hPart, mPart = '00'] = String(value).split('.');
    const h = Number(hPart);
    const m = Number((mPart || '00').padEnd(2, '0').slice(0, 2));
    if (Number.isNaN(h) || Number.isNaN(m)) return 0;
    return (h * 60) + Math.min(Math.max(m, 0), 59);
  };

  useEffect(() => { getEmployees().then(setMechanics).catch(() => {}); }, []);

  function getRangeDates() {
    const today = new Date();
    if (dateRange === 'custom') return { start: exportStart, end: exportEnd };
    if (dateRange === 'weekly') {
      const sun = new Date(today); sun.setDate(today.getDate() - today.getDay());
      const sat = new Date(sun);   sat.setDate(sun.getDate() + 6);
      return { start: formatFromDate(sun), end: formatFromDate(sat) };
    }
    if (dateRange === 'biweekly') {
      const sun  = new Date(today); sun.setDate(today.getDate() - today.getDay());
      const prev = new Date(sun);   prev.setDate(sun.getDate() - 7);
      const sat  = new Date(sun);   sat.setDate(sun.getDate() + 6);
      return { start: formatFromDate(prev), end: formatFromDate(sat) };
    }
    if (dateRange === 'monthly') {
      const s = new Date(today.getFullYear(), today.getMonth(), 1);
      const e = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { start: formatFromDate(s), end: formatFromDate(e) };
    }
    if (dateRange === 'yearly') {
      return { start: formatFromDate(new Date(today.getFullYear(), 0, 1)), end: formatFromDate(today) };
    }
    return { start: null, end: null };
  }

  const fetchSummary = useCallback(async () => {
    setLoadingSum(true);
    try   { setSummaryRows(await getWeeklySummary(summaryDate)); }
    catch (err) { showToast(err.message || 'Failed to load report', 'error'); }
    finally     { setLoadingSum(false); }
  }, [summaryDate, showToast]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  useEffect(() => {
    if (dateRange === 'custom' && (!exportStart || !exportEnd)) {
      setShowPreview(false); setExportRows([]); return;
    }
    const { start, end } = getRangeDates();
    if (!start || !end) return;
    setLoadingExp(true);
    getExportPreview(start, end, filterEmp)
      .then(data => { setExportRows(data); setShowPreview(true); })
      .catch(err => showToast(err.message || 'Failed to load export', 'error'))
      .finally(() => setLoadingExp(false));
  }, [dateRange, exportStart, exportEnd, filterEmp]);

  async function handleDownloadExcel() {
    if (dateRange === 'custom' && (!exportStart || !exportEnd)) { showToast('Select date range first', 'error'); return; }
    try   { const { start, end } = getRangeDates(); await downloadExcel(start, end, filterEmp); showToast('Excel downloaded ✓', 'success'); }
    catch (err) { showToast(err.message || 'Download failed', 'error'); }
  }
  async function handleDownloadPDF() {
    if (dateRange === 'custom' && (!exportStart || !exportEnd)) { showToast('Select date range first', 'error'); return; }
    try   { const { start, end } = getRangeDates(); await downloadPDF(start, end, filterEmp); showToast('PDF downloaded ✓', 'success'); }
    catch (err) { showToast(err.message || 'Download failed', 'error'); }
  }

  const tots = summaryRows.reduce((a, r) => ({
    total_minutes:    a.total_minutes    + toMinutes(r.total_hours    || 0),
    regular_minutes:  a.regular_minutes  + toMinutes(r.regular_hours  || 0),
    overtime_minutes: a.overtime_minutes + toMinutes(r.overtime_hours || 0),
    total_pay:      a.total_pay      + parseFloat(r.total_pay      || 0),
    regular_pay:    a.regular_pay    + parseFloat(r.regular_pay    || 0),
    overtime_pay:   a.overtime_pay   + parseFloat(r.overtime_pay   || 0),
  }), { total_minutes: 0, regular_minutes: 0, overtime_minutes: 0, total_pay: 0, regular_pay: 0, overtime_pay: 0 });

  const labelStyle = {
    fontSize: '0.68rem', fontWeight: 600, color: '#6b7280',
    textTransform: 'uppercase', letterSpacing: '.05em',
    marginBottom: '0.3rem', display: 'block',
  };
  const selectStyle = {
    width: '100%', boxSizing: 'border-box',
    border: '1.5px solid #d1d5db', borderRadius: '6px',
    padding: '0.5rem 0.7rem', fontSize: '0.875rem',
    color: '#111827', background: '#fff', outline: 'none',
  };
  const panelCard = {
    background: '#fff', border: '1px solid #e5e7eb',
    borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,.07)',
    // NOTE: no overflow:hidden — would clip the calendar popup
  };
  const panelHeader = {
    padding: '0.85rem 1.25rem', borderBottom: '1px solid #e5e7eb',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: '0.5rem',
  };

  const { start: rangeStart, end: rangeEnd } = getRangeDates();

  return (
    <div style={{ padding: '1.25rem', background: '#f3f4f6', minHeight: '100vh' }}>

      <div style={{ marginBottom: '1.25rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 800, margin: 0, color: '#111827', letterSpacing: '.02em' }}>
          REPORTS & EXPORT
        </h1>
        <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem', color: '#6b7280' }}>
          Generate payroll reports and export timesheet data
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem', alignItems: 'start' }}>

        {/* ══ Export Panel ══ */}
        <div style={panelCard}>
          <div style={panelHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>📊</span>
              <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#111827', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                Export Timesheets
              </span>
            </div>
          </div>

          <div style={{ padding: '1.1rem', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
            <div>
              <label style={labelStyle}>Date Range</label>
              <select style={selectStyle} value={dateRange}
                onChange={e => { setDateRange(e.target.value); setExportStart(''); setExportEnd(''); }}>
                <option value="weekly">Weekly (This Week)</option>
                <option value="biweekly">Bi-Weekly (This + Last Week)</option>
                <option value="monthly">Monthly (This Month)</option>
                <option value="yearly">Yearly (Jan 1 – Today)</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {dateRange !== 'custom' && rangeStart && rangeEnd && (
              <div style={{
                background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px',
                padding: '0.55rem 0.85rem', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.3rem',
              }}>
                <span style={{ fontSize: '0.68rem', fontWeight: 600, color: '#15803d', textTransform: 'uppercase', letterSpacing: '.04em' }}>Period</span>
                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#166534', fontFamily: 'monospace' }}>
                  {toDisplay(rangeStart)} — {toDisplay(rangeEnd)}
                </span>
              </div>
            )}

            {dateRange === 'custom' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
                <div>
                  <label style={labelStyle}>Start Date</label>
                  <DatePicker value={exportStart} onChange={setExportStart} />
                </div>
                <div>
                  <label style={labelStyle}>End Date</label>
                  <DatePicker value={exportEnd} onChange={setExportEnd} />
                </div>
              </div>
            )}

            <div>
              <label style={labelStyle}>Filter by Employee (Optional)</label>
              <select style={selectStyle} value={filterEmp} onChange={e => setFilterEmp(e.target.value)}>
                <option value="">All Employees</option>
                {mechanics.map(m => (
                  <option key={m.id} value={m.id}>{m.ee_number} – {m.last_name}, {m.first_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Group By</label>
              <select style={selectStyle} value={groupBy} onChange={e => setGroupBy(e.target.value)}>
                <option value="employee">Employee</option>
                <option value="department">Department</option>
                <option value="week">Week</option>
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
              {[
                { label: 'Excel (.xlsx)', icon: '📊', color: '#16a34a', handler: handleDownloadExcel },
                { label: 'PDF Report',   icon: '📋', color: '#dc2626', handler: handleDownloadPDF  },
              ].map(btn => (
                <button key={btn.label} onClick={btn.handler} style={{
                  background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '8px',
                  padding: '1rem 0.5rem', cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = btn.color; e.currentTarget.style.boxShadow = `0 0 0 3px ${btn.color}20`; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}>
                  <span style={{ fontSize: '1.6rem' }}>{btn.icon}</span>
                  <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#374151', letterSpacing: '.04em', textTransform: 'uppercase' }}>
                    {btn.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ══ Quick Summary Panel ══ */}
        <div style={panelCard}>
          <div style={panelHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
              <span>📝</span>
              <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#111827', textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap' }}>
                Quick Summary
              </span>
            </div>
            <div style={{ width: '150px', flexShrink: 0 }}>
              <DatePicker value={summaryDate} onChange={setSummaryDate} small />
            </div>
          </div>

          {loadingSum ? (
            <div style={{ padding: '3rem', textAlign: 'center' }}><Spinner /></div>
          ) : (
            <div style={{ overflowX: 'auto', maxHeight: '420px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    {[{ label: 'EE#', align: 'left' }, { label: 'Name', align: 'left' }, { label: 'Total Hrs', align: 'right' }].map(h => (
                      <th key={h.label} style={{
                        padding: '0.6rem 1rem', textAlign: h.align,
                        fontSize: '0.68rem', fontWeight: 600, color: '#6b7280',
                        textTransform: 'uppercase', letterSpacing: '.04em',
                      }}>{h.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {summaryRows.length === 0 ? (
                    <tr><td colSpan={3} style={{ padding: '2.5rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>
                      No approved timesheets for this week.
                    </td></tr>
                  ) : summaryRows.map(r => (
                    <tr key={r.week_id}
                      style={{ borderBottom: '1px solid #f3f4f6', background: '#fff' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                      <td style={{ padding: '0.6rem 1rem', fontFamily: 'monospace', color: '#dc2626', fontWeight: 600 }}>{r.ee_number}</td>
                      <td style={{ padding: '0.6rem 1rem', fontWeight: 500, color: '#111827' }}>{r.last_name}, {r.first_name}</td>
                      <td style={{ padding: '0.6rem 1rem', textAlign: 'right', fontWeight: 700, color: '#dc2626', fontFamily: 'monospace' }}>
                        {toHourMinuteString(toMinutes(r.total_hours))}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {summaryRows.length > 0 && (
                  <tfoot>
                    <tr style={{ background: '#f9fafb', borderTop: '2px solid #e5e7eb' }}>
                      <td colSpan={2} style={{ padding: '0.6rem 1rem', fontSize: '0.68rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase' }}>
                        Total ({summaryRows.length} mechanics)
                      </td>
                      <td style={{ padding: '0.6rem 1rem', textAlign: 'right', fontWeight: 700, color: '#dc2626', fontFamily: 'monospace' }}>
                        {toHourMinuteString(tots.total_minutes)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Export Preview ── */}
      {showPreview && (
        <div style={{ marginTop: '1rem', ...panelCard }}>
          <div style={panelHeader}>
            <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#111827', textTransform: 'uppercase', letterSpacing: '.04em' }}>
              Export Preview
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{exportRows.length} records</span>
              <button onClick={() => setShowPreview(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '1.1rem' }}>✕</button>
            </div>
          </div>

          {loadingExp ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}><Spinner /></div>
          ) : exportRows.length === 0 ? (
            <div style={{ padding: '2.5rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>
              No approved timesheets found for this period.
            </div>
          ) : (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: '0.875rem', minWidth: '700px', width: '100%' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    {[
                      { label: 'Week Ending', w: '120px' },
                      { label: 'EE#',         w: '70px'  },
                      { label: 'Name',        w: '170px' },
                      { label: 'Total Hrs',   w: '85px'  },
                      { label: 'Reg Hrs',     w: '85px'  },
                      { label: 'OT Hrs',      w: '85px'  },
                    ].map(h => (
                      <th key={h.label} style={{
                        padding: '0.6rem 1rem', textAlign: 'left',
                        fontSize: '0.68rem', fontWeight: 600, color: '#6b7280',
                        textTransform: 'uppercase', letterSpacing: '.04em',
                        whiteSpace: 'nowrap', width: h.w, minWidth: h.w,
                      }}>{h.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {exportRows.map((r, i) => (
                    <tr key={i}
                      style={{ borderBottom: '1px solid #f3f4f6', background: '#fff' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                      <td style={{ padding: '0.6rem 1rem', fontFamily: 'monospace', color: '#374151', whiteSpace: 'nowrap' }}>{r.week_ending_date}</td>
                      <td style={{ padding: '0.6rem 1rem', fontFamily: 'monospace', color: '#dc2626', fontWeight: 600, whiteSpace: 'nowrap' }}>{r.ee_number}</td>
                      <td style={{ padding: '0.6rem 1rem', fontWeight: 500, whiteSpace: 'nowrap' }}>{r.last_name}, {r.first_name}</td>
                      <td style={{ padding: '0.6rem 1rem', fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>{toHourMinuteString(toMinutes(r.total_hours))}</td>
                      <td style={{ padding: '0.6rem 1rem', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{toHourMinuteString(toMinutes(r.regular_hours))}</td>
                      <td style={{ padding: '0.6rem 1rem', whiteSpace: 'nowrap' }}>
                        {toMinutes(r.overtime_hours) > 0
                          ? <span style={{ color: '#dc2626', fontWeight: 600, fontFamily: 'monospace' }}>{toHourMinuteString(toMinutes(r.overtime_hours))}</span>
                          : <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {(() => {
                  const t = exportRows.reduce((a, r) => ({
                    total_minutes:    a.total_minutes    + toMinutes(r.total_hours    || 0),
                    regular_minutes:  a.regular_minutes  + toMinutes(r.regular_hours  || 0),
                    overtime_minutes: a.overtime_minutes + toMinutes(r.overtime_hours || 0),
                  }), { total_minutes: 0, regular_minutes: 0, overtime_minutes: 0 });
                  return (
                    <tfoot>
                      <tr style={{ background: '#f9fafb', borderTop: '2px solid #e5e7eb' }}>
                        <td colSpan={3} style={{ padding: '0.6rem 1rem', fontSize: '0.68rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                          Totals ({exportRows.length} records)
                        </td>
                        <td style={{ padding: '0.6rem 1rem', fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>{toHourMinuteString(t.total_minutes)}</td>
                        <td style={{ padding: '0.6rem 1rem', fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>{toHourMinuteString(t.regular_minutes)}</td>
                        <td style={{ padding: '0.6rem 1rem', fontFamily: 'monospace', fontWeight: 700, color: t.overtime_minutes > 0 ? '#dc2626' : '#111827', whiteSpace: 'nowrap' }}>
                          {toHourMinuteString(t.overtime_minutes)}
                        </td>
                      </tr>
                    </tfoot>
                  );
                })()}
              </table>
            </div>
          )}
        </div>
      )}

      <Toast message={toast.message} type={toast.type} onClose={clearToast} />
    </div>
  );
}